import {l} from "types/typescriptdefinisjoner_lederplan";
import {RessursFeilhåndtering} from "../RessursFeilhåndtering";
import {gettextCatalog} from "angular-gettext";
import _ from "lodash";
import {regNavn} from "../tredjepartsTjenestenavn";
import {Filer, Mimeinfo} from "../Filer";
import {Kontekst} from "../Kontekst";
import {Konfigurasjon} from "../../infrastruktur/Konfigurasjon";
import {uriEnkodet} from "../../stoettefunk/uri";
import {Debugger} from "debug";
import {resource} from "angular";
import {erIkkeNill} from "felles/stoettefunk/nillsjekk";

const debug: Debugger = require('debug')('DokumentRessurs');

/**
 * Hjelpemetoder for dokument ressurser.
 * Registrering og sletting av dokumenter håndteres via andre ressurser - feks SakRessurs / SakWebRessurs
 */
export class DokumentRessurs {
    static regNavn = "dokumentRessurs";

    static forInjisering() {
        return [
            Filer.regNavn,
            regNavn.ng.$resource,
            RessursFeilhåndtering.regNavn,
            Kontekst.regNavn,
            Konfigurasjon.regNavn,
            regNavn.gettext.gettextCatalog,
            DokumentRessurs
        ]
    }

    constructor(private filer: Filer,
                private $resource: ng.resource.IResourceService,
                private ressursFeilhåndtering: RessursFeilhåndtering,
                private kontekst: Kontekst,
                private konfigurasjon: Konfigurasjon,
                private gettextCatalog: gettextCatalog) {
        this.ressurs = $resource(
            "/api/dokument/:id",
            {id: '@id'},
            {
                hentGjeldendeDokumentversjon: {method: 'GET', url: '/api/dokument/hentGjeldendeDokumentversjon/:id'},
                hentGjeldendeSaksdokument: {method: 'GET', url: '/api/dokument/hentGjeldendeSaksdokument/:id'},
                hentDokumentVersjon: {method: 'GET', url: '/api/dokument/hentDokumentVersjon/:id'},
                hentSaksdokumentBasertPåDokumentAnnotering: {method: 'GET', url: '/api/dokument/hentSaksdokument/dokumentannotering/:annoteringId'},
                hentDokumentversjonBasertPåDokumentAnnotering: {method: 'GET', url: '/api/dokument/hentDokumentversjon/dokumentannotering/:annoteringId'},
                protokolldokumentdetaljer: {method: 'GET', url: '/api/dokument/:id/protokolldokumentdetaljer'},
                versjoner: {method: 'GET', url: '/api/dokument/:id/versjoner', isArray: true},
                annoteringer: {method: 'GET', url: '/api/dokument/:dokumentVersjonId/annoteringer', isArray: true},
                hentSkjerming: {method: 'GET', url: '/api/dokument/:dokumentId/skjerming', isArray: false},
                oppdaterSkjerming: {method: 'POST', url: '/api/dokument/:dokumentId/skjerming', isArray: false, params: {dokumentId:'@dokumentId'}}
            }
        );
    }


    public ressurs: ng.resource.IResourceClass<ng.resource.IResource<l.DokumentVersjonModell>>;

    hent(versjonId: string): l.SaksdokumentModell {
        debug("dokument.hent", versjonId);
        return <l.SaksdokumentModell><any>this.ressurs.get(
            {id: versjonId},
            _.noop,
            this.håndterFeil(() => this.gettextCatalog.getString('Kunne ikke laste dokument med versjons-id {{versjonId}}', {versjonId: versjonId}))
        );
    }

    hentGjeldendeDokumentversjon(dokumentId: string): l.DokumentVersjonModell {
        debug("hentGjeldendeDokumentversjon", dokumentId);
        return <l.SaksdokumentModell><any>this.ressurs['hentGjeldendeDokumentversjon'](
            {id: dokumentId},
            _.noop,
            this.håndterFeil(() => this.gettextCatalog.getString('Kunne ikke laste dokument med id {{dokumentId}}', {dokumentId:dokumentId}))
        );
    }

    hentGjeldendeSaksdokument(dokumentId: string, vedFeil: (feil: resource.IResourceResponse) => boolean = () => false): l.SaksdokumentModell {
        debug("hentGjeldendeSaksdokument", dokumentId);
        return <l.SaksdokumentModell><any>this.ressurs['hentGjeldendeSaksdokument'](
          {id: dokumentId},
          _.noop,
          this.håndterFeil(() => this.gettextCatalog.getString('Kunne ikke laste dokument med id {{dokumentId}}', {dokumentId:dokumentId}), vedFeil)
        );
    }


    hentDokumentVersjon(dokumentVersjonId: string): l.DokumentVersjonModell {
        debug("hentDokumentVersjon", dokumentVersjonId);
        return <l.DokumentVersjonModell><any>this.ressurs['hentDokumentVersjon'](
          {id: dokumentVersjonId},
          _.noop,
          this.håndterFeil(() => this.gettextCatalog.getString('Kunne ikke laste dokumentVersjon med id {{dokumentVersjonId}}', {dokumentVersjonId: dokumentVersjonId}))
        );
    }

    hentSaksdokumentBasertPåDokumentAnnotering(annoteringId: string): l.SaksdokumentModell {
        debug("dokument.hentSaksdokumentBasertPåDokumentAnnotering", annoteringId);
        return <l.SaksdokumentModell><any>this.ressurs['hentSaksdokumentBasertPåDokumentAnnotering'](
            {annoteringId: annoteringId},
            _.noop,
            this.håndterFeil(() => this.gettextCatalog.getString('Kunne ikke laste dokument med annoterings-id {{annoteringId}}', {annoteringId:annoteringId}))
        );
    }

    hentDokumentversjonBasertPåDokumentAnnotering(annoteringId: string): l.DokumentVersjonModell {
        debug("dokument.hentDokumentversjonBasertPåDokumentAnnotering", annoteringId);
        return <l.SaksdokumentModell><any>this.ressurs['hentDokumentversjonBasertPåDokumentAnnotering'](
            {annoteringId: annoteringId},
            _.noop,
            this.håndterFeil(() => this.gettextCatalog.getString('Kunne ikke laste dokumentannotering med annoterings-id {{annoteringId}}', {annoteringId:annoteringId}))
        );
    }

    hentProtokolldokumentdetaljer(id: string): l.ProtokolldokumentISakModell {
        debug("dokument.hentProtokolldokumentdetaljer", id);
        return <l.ProtokolldokumentISakModell><any>this.ressurs['protokolldokumentdetaljer'](
            {id: id},
            _.noop,
            this.håndterFeil(() => this.gettextCatalog.getString('Kunne ikke laste dokument med id {{dokumentId}}', {dokumentId:id}))
        );
    }

    hentVersjoner(id: string): l.DokumentVersjonModell[] {
        debug("dokument.hentVersjoner", id);
        return this.ressurs["versjoner"](
            {id: id},
            _.noop,
            this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke laste dokumentversjoner")));
    }

    annoteringer(dokumentVersjonId: string): l.DokumentAnnoteringModell[] {
        debug("dokument.annoteringer", dokumentVersjonId);
        return this.ressurs["annoteringer"](
          {dokumentVersjonId: dokumentVersjonId},
          _.noop,
          this.håndterFeil(()=>this.gettextCatalog.getString("Kunne ikke laste annoteringer")));
    }

    dokumentinformasjon(dokumentversjon: l.DokumentVersjonBasisModell<any>): DokumentInformasjon {
        let orginalFormat = this.filer.finnMimeinfoBasertPåMimetype(dokumentversjon.orginalformat);
        const orginalFormatInfo = this.formatInfo(dokumentversjon, dokumentversjon.filnavn, orginalFormat);
        const pdfFormatInfo = this.formatInfo(dokumentversjon, dokumentversjon.pdfFilnavn, Filer.mimetyper.pdf);

        let formater = [orginalFormatInfo];
        if (dokumentversjon.harPdfRepresentasjon && orginalFormat !== Filer.mimetyper.pdf)
            formater.push(this.formatInfo(dokumentversjon, dokumentversjon.pdfFilnavn, Filer.mimetyper.pdf));

        const dokumentInformasjon = new DokumentInformasjon();
        dokumentInformasjon.virussjekkPaagaar = dokumentversjon.virussjekkStatus == l.VirussjekkStatus.ikke_sjekket;
        dokumentInformasjon.infisert = dokumentversjon.virussjekkStatus == l.VirussjekkStatus.infisert;
        dokumentInformasjon.forsideUrl = this.forsideUrl(dokumentversjon);
        dokumentInformasjon.forsideSti = this.forsideSti(dokumentversjon);
        dokumentInformasjon.formater = formater;
        dokumentInformasjon.multipleFormater = formater.length > 0;
        dokumentInformasjon.standardformat = dokumentversjon.harPdfRepresentasjon ? pdfFormatInfo : orginalFormatInfo;
        dokumentInformasjon.orginalFormat = orginalFormatInfo;
        dokumentInformasjon.erProtokolldokumentISak = dokumentversjon.type == l.DokumentType.protokolldokumentISak;
        dokumentInformasjon.erGenerertAgendaDokument = dokumentversjon.type == l.DokumentType.generertAgendaDokument;
        return dokumentInformasjon;
    }

    dokumentUrl(dokumentVersjonId: string, mimetype: string, filnavn: string): string { //@todo gjøre filnavn optional? - hvordan takler serversiden det?
        return `${this.konfigurasjon.serviceRotUrl}/api/dokument/data/${dokumentVersjonId}/${uriEnkodet(mimetype)}/${uriEnkodet(filnavn)}`;
    }

    pdfjsDokumentViserUrl(dokumentversjon: l.DokumentVersjonModell, forespurtAnnoteringId: string = null) {
        //vi bygger to versjoner av pdf.js - en for moderne nettlesere og en for eldre - sjekker her hvilken vi skal servere
        const legacy = this.brukPdfJsLegacy();
        let url = `${this.konfigurasjon.serviceRotUrl}/app/visdokument/pdf${legacy ? '-legacy':''}/versjon/${dokumentversjon.id}/${uriEnkodet(Filer.mimetyper.pdf.mimetype)}/${uriEnkodet(dokumentversjon.pdfFilnavn)}`;
        if (forespurtAnnoteringId) url = `${url}?forespurtAnnoteringId=${forespurtAnnoteringId}`;
        return url;
    }

    pdfjsDokumentViserUrlForMobil(dokumentVersjonId: string, fileUrl: string, tilgangstoken: string): string {
        const parameter = _({
            file: fileUrl,
            dokumentVersjonId: dokumentVersjonId,
            serviceRotUrl: this.konfigurasjon.serviceRotUrl,
            token: tilgangstoken
        })
            .mapValues((verdi: string, nøkkel: string) => `${nøkkel}=${uriEnkodet(verdi)}`)
            .values()
            .join('&');

        //vi bygger to versjoner av pdf.js - en for moderne nettlesere og en for eldre - sjekker her hvilken vi skal servere
        const legacy = this.brukPdfJsLegacy();
        return `/lib/pdfjs/dist${legacy ? '-legacy':''}/web/viewer.html?${parameter}`;
    }

    hentSkjerming(dokumentId: string): l.SkjermingDetaljerModell {
        debug("hentSkjerming", dokumentId);
        return <l.SkjermingDetaljerModell><any>this.ressurs['hentSkjerming'](
            {dokumentId: dokumentId},
            _.noop,
            this.håndterFeil(() => this.gettextCatalog.getString('Kunne ikke hente dokument'))
        );
    }

    oppdaterSkjerming(dokumentId: string, spesifikasjon:l.OppdaterSkjermingSpesifikasjon): l.SkjermingDetaljerMedValideringsresultatModell<l.SkjermingValideringsfeil> {
        debug("oppdaterSkjerming", dokumentId);
        return this.ressurs['oppdaterSkjerming'](
            _.defaults({dokumentId: dokumentId}, spesifikasjon),
            _.noop,
            this.håndterFeil(() => this.gettextCatalog.getString('Kunne ikke oppdatere tilgangsbegrensning'))
        );
    }

    /**
     * pdfjs oppgir at Safari >= 16.4 er støttet i legacy-bygg. String.isWellFormed ble introdusert i Safari 16.4,
     * Chrome og Edge 111 (Mars 2023), Firefox 119 (Oktober 2023).
     */
    støtterPdfjs() {
        return erIkkeNill(String.prototype['isWellFormed']);
    }

    private formatInfo(dokumentversjon: l.DokumentVersjonModell, filnavn: string, mimeInfo: Mimeinfo): DokumentFormatInfo {
        return {
            filnavn: filnavn,
            mimeinfo: mimeInfo,
            url: this.dokumentUrl(dokumentversjon.id, mimeInfo.mimetype, filnavn)
        };
    }

    private forsideSti(dokumentversjon: l.DokumentVersjonModell): string {
        return `/api/dokument/${dokumentversjon.id}/forside?format=png&bredde=130`
    }

    private forsideUrl(dokumentversjon: l.DokumentVersjonModell): string {
        return `${this.konfigurasjon.serviceRotUrl}${this.forsideSti(dokumentversjon)}`
    }

    private håndterFeil(melding: () => string,
                eksternHåndtering: (feil: resource.IResourceResponse) => boolean = () => false): (feil: resource.IResourceResponse) => void {
        return this.ressursFeilhåndtering.håndterRequestFeil(melding, null, eksternHåndtering);
    }

    /**
     * Vi bygger to versjoner av pdf.js - en for moderne nettlesere og en for eldre.
     * <pre>
     * Antar legacy dersom Promise.withResolvers ikke eksisterer, se dokumentasjon i
     * vårt pdfjs prosjekt ( https://github.com/systemfabrikken/lederplan-pdfjs ),
     * under aktiv annotering-branch i filen /sfannotering/README.md
     * 
     * Merk at angular patcher bla Promise objektet via zone.js,
     * derfor __zone_symbol__Promise sjekken.
     *
     * Promise.withResolvers ble introdusert i Ecmascript 2023
     * og tatt i bruk i pdfjs i 4.2.67
     * </pre>
     */
    private brukPdfJsLegacy(){
        if (window['__zone_symbol__Promise'] && !window['__zone_symbol__Promise']['withResolvers']) return true;
        if (!Promise['withResolvers']) return true;
        return false;
    }
}

export class DokumentInformasjon {
    virussjekkPaagaar: boolean;
    infisert: boolean;
    forsideUrl: string;
    forsideSti: string;
    formater: DokumentFormatInfo[];
    multipleFormater: boolean;
    standardformat: DokumentFormatInfo;
    orginalFormat: DokumentFormatInfo;
    erProtokolldokumentISak: boolean;
    erGenerertAgendaDokument: boolean;

    formatFor(mimetype: string):DokumentFormatInfo {
        return _(this.formater).find(format => format.mimeinfo.mimetype === mimetype);
    }
}

export interface DokumentFormatInfo {
    filnavn: string;
    mimeinfo: Mimeinfo;
    url: string;
}
